import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import useQueryParams from './useQueryParams'
import { PanelSupplierCode } from '../model/common'

export interface QuestionnaireParams {
  respondentId: string
  surveyId: string
  panelSupplier: string
  preview: boolean
  // True when `rid` was auto-generated by the app (ps=fd or preview with no
  // `rid` in the URL) rather than supplied by the panel. Tracked in
  // sessionStorage (see below) so end-of-survey logic never redirects a fake,
  // app-generated RID back to a supplier.
  respondentIdAutoGenerated: boolean
}

// sessionStorage key (scoped by survey) recording that the app generated the
// `rid` for this session. sessionStorage survives the reload triggered by
// generateRespondentId (same tab) but — unlike a URL marker — is not visible or
// editable in the address bar and never leaks into other URLs built from
// `location.search` (e.g. useFdChatUrl / participant-experience redirects).
const generatedRidStorageKey = (surveyId: string) =>
  `fd_generated_rid_${surveyId}`

const markRespondentIdAsGenerated = (surveyId: string, rid: string) => {
  try {
    window.sessionStorage.setItem(generatedRidStorageKey(surveyId), rid)
  } catch {
    // sessionStorage unavailable (e.g. privacy mode): the redirect gate will
    // treat the RID as supplier-provided. Acceptable — worst case is a redirect
    // to the supplier with a non-real RID, never an over-credit.
  }
}

const wasRespondentIdGenerated = (surveyId: string, rid: string): boolean => {
  try {
    return (
      window.sessionStorage.getItem(generatedRidStorageKey(surveyId)) === rid
    )
  } catch {
    return false
  }
}

const generateRespondentId = (queryParams: URLSearchParams) => {
  const rid = uuidv4()
  queryParams.set('rid', rid)

  const surveyId = queryParams.get('sid')
  if (surveyId) {
    markRespondentIdAsGenerated(surveyId, rid)
  }

  window.location.search = queryParams.toString()

  return rid
}

export const isPreviewMode = (queryParams: URLSearchParams) => {
  return queryParams.get('preview') === 'true'
}

export const getQuestionnaireQueryParams = (
  queryParams: URLSearchParams
): QuestionnaireParams | null => {
  const urlRespondentId = queryParams.get('rid')
  const urlPanelSupplierCode = queryParams.get('ps')
  const shouldGenerateRespondentId =
    (urlPanelSupplierCode === PanelSupplierCode.Fd ||
      isPreviewMode(queryParams)) &&
    !urlRespondentId

  const respondentId: string | null = shouldGenerateRespondentId
    ? generateRespondentId(queryParams)
    : urlRespondentId
  const surveyId: string | null = queryParams.get('sid')
  const panelSupplier: string | null = queryParams.get('ps')

  if (!respondentId || !surveyId || !panelSupplier) {
    return null
  }

  const respondentIdAutoGenerated =
    shouldGenerateRespondentId ||
    wasRespondentIdGenerated(surveyId, respondentId)

  return {
    respondentId,
    surveyId,
    panelSupplier,
    preview: isPreviewMode(queryParams),
    respondentIdAutoGenerated
  }
}

export const useOptionalQuestionnaireParams =
  (): QuestionnaireParams | null => {
    const queryParams = useQueryParams()

    const [state] = useState(() => getQuestionnaireQueryParams(queryParams))

    return state
  }

export const useQuestionnaireParams = (): QuestionnaireParams => {
  const queryParams = useQueryParams()

  const [state] = useState(() => {
    const params = getQuestionnaireQueryParams(queryParams)

    if (!params) {
      throw new Error('Query params missing, cannot render survey')
    }

    return params
  })

  return state
}
